import React, { useRef, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";

const PhotoUploadZone = ({
  uploadedFile,
  setUploadedFile,
  uploadProgress,
  setUploadProgress,
  isUploading,
  setIsUploading,
  errors,
  setErrors,
  onFileSelect,
  onVideoModeSelect,
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);

  // Mode: photo | video
  const [mode, setMode] = useState("photo");

  /* -------------------------------
     IMAGE VALIDATION
  -------------------------------- */
  const validateFile = (file) => {
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    const maxSize = 10 * 1024 * 1024;

    if (!file || !validTypes.includes(file.type))
      return "Please upload a JPG or PNG image file";
    if (file.size > maxSize)
      return "File size must be less than 10MB";

    return null;
  };

  /* -------------------------------
     IMAGE FILE SELECT
  -------------------------------- */
  const handleFileSelect = (file) => {
    const error = validateFile(file);
    if (error) {
      setErrors((prev) => ({ ...prev, file: error }));
      return;
    }

    setErrors((prev) => ({ ...prev, file: null }));
    setIsUploading(true);
    setUploadProgress(0);

    const uploadInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(uploadInterval);
          setIsUploading(false);
          setUploadedFile(file);

          const reader = new FileReader();
          reader.onload = (e) => setPreview(e.target.result);
          reader.readAsDataURL(file);

          if (onFileSelect) onFileSelect(file);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
  };

  /* -------------------------------
     DRAG EVENTS
  -------------------------------- */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0])
      handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files?.[0])
      handleFileSelect(e.target.files[0]);
  };

  /* -------------------------------
     REMOVE IMAGE
  -------------------------------- */
  const removeFile = () => {
    setUploadedFile(null);
    setPreview(null);
    setUploadProgress(0);
    setErrors((prev) => ({ ...prev, file: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = (bytes / Math.pow(k, i)).toFixed(2);
    return `${size} ${["Bytes", "KB", "MB"][i]}`;
  };

  /* ======================================================
     UI
  ======================================================= */
  return (
    <div className="space-y-5">

      {/* MODE SWITCH */}
      <div
        className="inline-flex p-1 rounded-xl gap-1"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {["photo", "video"].map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 flex items-center gap-2"
            style={
              mode === m
                ? {
                    background: "linear-gradient(135deg, #1D4ED8, #0EA5E9)",
                    color: "#fff",
                    boxShadow: "0 4px 12px rgba(29,78,216,0.35)",
                  }
                : {
                    background: "transparent",
                    color: "rgba(255,255,255,0.45)",
                  }
            }
          >
            <Icon
              name={m === "photo" ? "Camera" : "Video"}
              size={14}
              color={mode === m ? "white" : "rgba(255,255,255,0.4)"}
            />
            {m === "photo" ? "Upload Photo" : "Upload Video"}
          </button>
        ))}
      </div>

      {/* ======================================================
         MODE: VIDEO
      ======================================================= */}
      {mode === "video" && (
        <div
          className="rounded-xl p-8 text-center flex flex-col items-center space-y-5"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, rgba(59,130,246,0.2), rgba(6,182,212,0.12))",
              border: "1px solid rgba(59,130,246,0.28)",
            }}
          >
            <Icon name="Video" size={28} color="#60A5FA" />
          </div>

          <div>
            <h3 className="text-lg font-light mb-2" style={{ color: "#E2E8F0" }}>
              Video-Based Graft Analysis
            </h3>
            <p className="text-sm max-w-md leading-relaxed" style={{ color: "#64748B" }}>
              Upload a recorded scalp video from your device. The AI will analyze
              graft distribution across frames for enhanced accuracy.
            </p>
          </div>

          <button
            onClick={onVideoModeSelect}
            className="flex items-center gap-2.5 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-150"
            style={{
              background: "rgba(59,130,246,0.15)",
              border: "1px solid rgba(59,130,246,0.3)",
              color: "#93C5FD",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(59,130,246,0.25)";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(59,130,246,0.15)";
              e.currentTarget.style.color = "#93C5FD";
            }}
          >
            <Icon name="Video" size={16} color="currentColor" />
            Upload & Analyze Video
          </button>
        </div>
      )}

      {/* ======================================================
         MODE: PHOTO
      ======================================================= */}
      {mode === "photo" && (
        <>
          {!uploadedFile ? (
            <div
              className="relative rounded-xl p-10 text-center transition-all duration-200 border-2 border-dashed"
              style={{
                borderColor: dragActive
                  ? "rgba(6,182,212,0.7)"
                  : "rgba(255,255,255,0.12)",
                background: dragActive
                  ? "rgba(6,182,212,0.05)"
                  : "rgba(255,255,255,0.02)",
                transform: dragActive ? "scale(1.01)" : "scale(1)",
                minHeight: "240px",
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFileInputChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isUploading}
              />

              <div className="flex flex-col items-center justify-center space-y-4">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-200"
                  style={{
                    background: dragActive
                      ? "rgba(6,182,212,0.2)"
                      : "rgba(255,255,255,0.06)",
                    border: dragActive
                      ? "1px solid rgba(6,182,212,0.5)"
                      : "1px solid rgba(255,255,255,0.1)",
                    transform: dragActive ? "scale(1.1)" : "scale(1)",
                  }}
                >
                  <Icon
                    name="Upload"
                    size={26}
                    color={dragActive ? "#22D3EE" : "rgba(255,255,255,0.5)"}
                  />
                </div>

                <div className="space-y-1.5">
                  <h3 className="text-base font-medium" style={{ color: "#E2E8F0" }}>
                    {dragActive ? "Drop your image here" : "Upload Patient Photo"}
                  </h3>
                  <p className="text-sm max-w-sm leading-relaxed" style={{ color: "#64748B" }}>
                    Use high-quality scalp photos under{" "}
                    <span style={{ color: "#94A3B8" }}>medical lighting</span>.
                    Avoid shadows, blur, and noise.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <div
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium pointer-events-none"
                    style={{
                      background: "rgba(59,130,246,0.15)",
                      border: "1px solid rgba(59,130,246,0.28)",
                      color: "#93C5FD",
                    }}
                  >
                    <Icon name="FolderOpen" size={15} color="#93C5FD" />
                    Choose File
                  </div>
                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.22)" }}>
                    or drag & drop
                  </span>
                  <span className="text-xs" style={{ color: "rgba(255,255,255,0.18)" }}>
                    JPG · PNG · Max 10MB
                  </span>
                </div>
              </div>
            </div>
          ) : (
            /* ── PREVIEW CARD ── */
            <div
              className="rounded-xl p-5"
              style={{
                background: "rgba(16,185,129,0.05)",
                border: "1px solid rgba(16,185,129,0.2)",
              }}
            >
              <div className="flex items-start gap-4">
                {/* Thumbnail */}
                <div
                  className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0"
                  style={{ border: "1px solid rgba(255,255,255,0.12)" }}
                >
                  <img
                    src={preview}
                    alt="Upload preview"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1 min-w-0 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h4
                        className="font-medium text-sm truncate mb-0.5"
                        style={{ color: "#E2E8F0" }}
                      >
                        {uploadedFile?.name}
                      </h4>
                      <div className="flex items-center gap-3 text-xs" style={{ color: "#64748B" }}>
                        <span className="flex items-center gap-1">
                          <Icon name="HardDrive" size={11} color="#64748B" />
                          {formatFileSize(uploadedFile?.size)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Image" size={11} color="#64748B" />
                          {uploadedFile?.type?.split("/")?.[1]?.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <button
                      onClick={removeFile}
                      className="flex-shrink-0 p-1.5 rounded-lg transition-all duration-100"
                      style={{
                        background: "rgba(220,38,38,0.1)",
                        border: "1px solid rgba(220,38,38,0.2)",
                        color: "#F87171",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(220,38,38,0.2)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(220,38,38,0.1)";
                      }}
                    >
                      <Icon name="Trash2" size={14} color="#F87171" />
                    </button>
                  </div>

                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{
                      background: "rgba(16,185,129,0.1)",
                      border: "1px solid rgba(16,185,129,0.2)",
                    }}
                  >
                    <Icon name="CheckCircle" size={14} color="#34D399" />
                    <span className="text-xs font-medium" style={{ color: "#34D399" }}>
                      Upload Successful
                    </span>
                    <span className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
                      · Ready for analysis
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* UPLOAD PROGRESS */}
          {isUploading && (
            <div
              className="rounded-xl p-4"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, #1D4ED8, #0EA5E9)" }}
                >
                  <Icon name="Upload" size={14} color="white" className="animate-bounce" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span style={{ color: "rgba(255,255,255,0.6)" }}>Uploading image...</span>
                    <span style={{ color: "#06B6D4", fontFamily: "'JetBrains Mono', monospace" }}>
                      {Math.round(uploadProgress)}%
                    </span>
                  </div>
                  <div
                    className="w-full h-1 rounded-full overflow-hidden"
                    style={{ background: "rgba(255,255,255,0.07)" }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${uploadProgress}%`,
                        background: "linear-gradient(90deg, #2563EB, #06B6D4)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ERROR */}
          {errors?.file && (
            <div
              className="rounded-xl p-4 flex items-start gap-3"
              style={{
                background: "rgba(220,38,38,0.08)",
                border: "1px solid rgba(220,38,38,0.25)",
              }}
            >
              <Icon name="AlertCircle" size={16} color="#F87171" />
              <div>
                <p className="text-xs font-semibold mb-0.5" style={{ color: "#FCA5A5" }}>
                  Upload Error
                </p>
                <p className="text-xs" style={{ color: "#F87171" }}>
                  {errors?.file}
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PhotoUploadZone;
